import { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadSimple, Warning, ClockCounterClockwise } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import { useUploadNewVersion } from '../../services/documents.service';

interface UploadNewVersionModalProps {
  companyId: string;
}

export interface UploadNewVersionModalRef {
  open: (documentId: string, documentName: string) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const UploadNewVersionModal = forwardRef<
  UploadNewVersionModalRef,
  UploadNewVersionModalProps
>(({ companyId }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const uploadNewVersionMutation = useUploadNewVersion(companyId);

  useImperativeHandle(ref, () => ({
    open: (docId: string, docName: string) => {
      setDocumentId(docId);
      setDocumentName(docName);
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setFileError(null);
      setIsDragOver(false);
      setDocumentId(null);
      setDocumentName('');
    }
  }, [isOpen]);

  const validateFile = useCallback(
    (file: File): boolean => {
      setFileError(null);

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        setFileError(t('documents.upload_new_version_modal.errors.invalid_format'));
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        setFileError(t('documents.upload_new_version_modal.errors.file_too_large'));
        return false;
      }

      return true;
    },
    [t],
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    },
    [validateFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleSubmit = () => {
    if (!selectedFile || !documentId) return;

    uploadNewVersionMutation.mutate({ documentId, file: selectedFile }, { onSuccess: handleClose });
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError(null);
    setIsOpen(false);
  };

  const isLoading = uploadNewVersionMutation.isPending;
  const canSubmit = !!selectedFile && !fileError;

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 bg-gray-100 rounded-md text-gray-900 font-medium hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]"
        disabled={isLoading}
      >
        {t('documents.upload_new_version_modal.cancel')}
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md font-medium hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isLoading || !canSubmit}
      >
        {isLoading
          ? t('documents.upload_new_version_modal.uploading')
          : t('documents.upload_new_version_modal.submit')}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('documents.upload_new_version_modal.title')}
      footer={footer}
      maxWidth="lg"
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {t('documents.upload_new_version_modal.document_name')}
          </label>
          <p className="text-gray-900 font-medium">{documentName}</p>
        </div>

        <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <Warning size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-orange-800">
            {t('documents.upload_new_version_modal.warning')}
          </p>
        </div>

        {documentId && (
          <button
            type="button"
            onClick={() =>
              window.open(
                `/documents/versions/${documentId}?name=${encodeURIComponent(documentName)}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            className="inline-flex items-center gap-2 self-start text-sm font-medium text-primary hover:underline"
          >
            <ClockCounterClockwise size={18} weight="bold" />
            {t('documents.upload_new_version_modal.view_versions')}
          </button>
        )}

        <div className="flex flex-col gap-3">
          <label className="text-base font-medium text-gray-900">
            {t('documents.upload_new_version_modal.dropzone.label')}
          </label>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
                flex flex-col items-center justify-center gap-3 p-6
                border border-dashed rounded-md transition-colors
                ${isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white'}
                ${selectedFile ? 'border-green-500 bg-green-50' : ''}
              `}
          >
            <UploadSimple size={48} className="text-gray-400" />

            {selectedFile ? (
              <div className="text-center">
                <p className="text-sm text-gray-900 font-medium">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} Mo
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center">
                {t('documents.upload_new_version_modal.dropzone.hint')}
              </p>
            )}

            {fileError && <p className="text-sm text-red-600">{fileError}</p>}

            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={handleInputChange}
                className="hidden"
              />
              <span className="inline-flex px-4 py-2 bg-gray-100 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]">
                {t('documents.upload_new_version_modal.dropzone.button')}
              </span>
            </label>
          </div>
        </div>
      </div>
    </BaseModal>
  );
});

UploadNewVersionModal.displayName = 'UploadNewVersionModal';
